import React, { memo, useContext, useEffect, useReducer } from 'react';
import { useSnackbar } from 'notistack';
import { useParams, Redirect } from 'react-router-dom';
import { sendArticleAnalytics } from '../../../catalog/Analytics';
import { WebsiteDesignType } from '../../../catalog/enums/WebsiteDesignType';
import { isValidObject, isValidString } from '../../../catalog/Validity';
import { context } from '../../../globalState/GlobalState';
import { fetchArticleAPI, fetchArticleTitleAPI } from '../../apiCalls/ArticleAPICalls';
import { fetchGalleryAPI } from '../../apiCalls/GalleryAPICalls';
import { fetchIceHockeyGameAPI, fetchLacrosseGameAPI, fetchRugbyGameAPI } from '../../apiCalls/games/GameAPICalls';
import Article1 from '../../designs/article1/Article1';
import { createCustomDescriptionTag, createCustomKeywordsTag, createCustomTitleTag } from '../uiElements/helmet/MetaTags';
import TitleAndDescriptionTags from '../uiElements/helmet/TitleAndDescriptionTags';
import { SportsType } from '../../../catalog/enums/SportsType';
import ArticleTags from '../uiElements/helmet/ArticleTags';

const initialState = {
    title: '',
    author: '',
    featureImage: '',
    content: '',
    mobileContent: '',
    published: false,
    timeCreated: new Date(),
    timeOfArticle: new Date(),
    galleryId: '',
    photos: [],
    galleryPublished: false,
    game: {},
    gameId: '',
    opponentName: '',
    opponentLogo: '',
    gameStatus: 'tba',
    homeGame: false,
    timeOfGame: {},
    loadingSpinner: true,
    secondaryLoadingSpinner: true,
    galleryLoadingSpinner: true,
    gameLoadingSpinner: true,
    error: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'fetchArticleTitle':
            return { ...state, title: action.title };
        case 'fetchArticle':
            return { ...state, title: action.title, author: action.author, content: action.content, mobileContent: action.mobileContent, published: action.published, featureImage: action.featureImage, timeOfArticle: action.timeOfArticle, galleryId: action.galleryId, gameId: action.gameId };
        case 'fetchGallery':
            return { ...state, photos: action.photos, galleryPublished: action.published };
        case 'fetchGame':
            return { ...state, game: action.game, timeOfGame: action.timeOfGame, opponentName: action.opponentName, opponentLogo: action.opponentLogo, homeGame: action.homeGame, gameStatus: action.status };
        case 'setLoadingSpinner':
            return { ...state, loadingSpinner: action.loadingSpinner };
        case 'setSecondaryLoadingSpinner':
            return { ...state, secondaryLoadingSpinner: action.loadingSpinner };
        case 'setGalleryLoadingSpinner':
            return { ...state, galleryLoadingSpinner: action.loadingSpinner };
        case 'setGameLoadingSpinner':
            return { ...state, gameLoadingSpinner: action.loadingSpinner };
        case 'setError':
            return { ...state, error: true };
        default:
            return initialState;
    }
}

const Article = () => {
    const params = useParams();
    const [state, setState] = useReducer(reducer, initialState);
    const { globalState } = useContext(context);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        sendArticleAnalytics(globalState, state.title, state.author, params.articleid);
    }, [globalState, state.title]);

    useEffect(() => {
        async function fetchArticle() {
            if (isValidObject(globalState.accountId)) {
                setState({ type: 'setLoadingSpinner', loadingSpinner: true });
                setState({ type: 'setSecondaryLoadingSpinner', loadingSpinner: true });
                await fetchArticleTitleAPI(params.articleid, setState, enqueueSnackbar);
                setState({ type: 'setLoadingSpinner', loadingSpinner: false });
                await fetchArticleAPI(globalState.accountId, params.articleid, setState, enqueueSnackbar);
                setState({ type: 'setSecondaryLoadingSpinner', loadingSpinner: false });
            }
        }

        fetchArticle();
    }, [globalState.accountId, params.articleid]);

    useEffect(() => {
        async function fetchGallery() {
            if (isValidObject(globalState.accountId) && isValidString(state.galleryId)) {
                setState({ type: 'setGalleryLoadingSpinner', loadingSpinner: true });
                await fetchGalleryAPI(globalState.accountId, state.galleryId, setState, enqueueSnackbar);
                setState({ type: 'setGalleryLoadingSpinner', loadingSpinner: false });
            }
        }
        fetchGallery();
    }, [state.galleryId]);

    useEffect(() => {
        async function fetchGame() {
            if (isValidObject(globalState.accountId) && isValidString(state.gameId)) {
                setState({ type: 'setGameLoadingSpinner', loadingSpinner: true });
                if (globalState.sportsType === SportsType.WOMENSICEHOCKEY || globalState.sportsType === SportsType.MENSICEHOCKEY) {
                    await fetchIceHockeyGameAPI(globalState.accountId, state.gameId, setState, enqueueSnackbar);
                } else if (globalState.sportsType === SportsType.WOMENSRUGBY || globalState.sportsType === SportsType.MENSRUGBY) {
                    await fetchRugbyGameAPI(globalState.accountId, state.gameId, setState, enqueueSnackbar);
                } else if (globalState.sportsType === SportsType.WOMENSLACROSSE || globalState.sportsType === SportsType.MENSLACROSSE) {
                    await fetchLacrosseGameAPI(globalState.accountId, state.gameId, setState, enqueueSnackbar);
                }
                setState({ type: 'setGameLoadingSpinner', loadingSpinner: false });
            }
        }
        fetchGame();
    }, [state.gameId]);

    const getArticleDesign = () => {
        if (!isValidObject(globalState) || !isValidObject(globalState.websiteStyles)) {
            return null;
        }

        if (globalState.websiteStyles.design.article === WebsiteDesignType.DESIGN_1) {
            return (
                <Article1
                    id={params.articleid}
                    title={state.title}
                    author={state.author}
                    featureImage={state.featureImage}
                    content={state.content}
                    mobileContent={state.mobileContent}
                    timeOfArticle={state.timeOfArticle}
                    published={state.published}
                    photos={state.photos}
                    galleryId={state.galleryId}
                    galleryPublished={state.galleryPublished}
                    game={state.game}
                    gameId={state.gameId}
                    opponentName={state.opponentName}
                    opponentLogo={state.opponentLogo}
                    homeGame={state.homeGame}
                    gameStatus={state.gameStatus}
                    timeOfGame={state.timeOfGame}
                    loadingSpinner={state.loadingSpinner}
                    secondaryLoadingSpinner={state.secondaryLoadingSpinner}
                    galleryLoadingSpinner={state.galleryLoadingSpinner}
                    gameLoadingSpinner={state.gameLoadingSpinner}
                />
            );
        }
        return null;
    };

    if (state.error) {
        return <Redirect to="/notfound" />;
    }

    return (
        <>
            {isValidString(state.title) ? (
                <>
                    <TitleAndDescriptionTags
                        title={createCustomTitleTag(state.title, globalState)}
                        description={createCustomDescriptionTag(`Check out this article - ${state.title}`, globalState)}
                        keywords={createCustomKeywordsTag(`News, Article, ${state.title}`, globalState)}
                    />
                    <ArticleTags id={params.articleid} title={state.title} articleTime={state.timeOfArticle} featureImage={state.featureImage} writerName={state.author} />
                </>
            ) : null}
            {getArticleDesign()}
        </>
    );
};

export default memo(Article);
